/** In-memory original commands. No persistence, auto-dispatch or GET inference. */
import { assertSafeWireNumbers, coachingManualClient } from './client';
import type { CoachingTransport } from './client';
import type { CoachingScopeIntent } from './scope';
import type { components } from './api.generated';

type Schema = components['schemas'];
export type CoachingManualCommand =
  | { action: 'create-goal-from-feedback'; body: Schema['CreateGoalFromFeedbackRequest'] }
  | { action: 'select-focus'; activityId: string; playerId: string; body: Schema['SelectCanonicalFocusRequest'] }
  | { action: 'create-support'; body: Schema['CreateAuthoredSupportRequest'] }
  | { action: 'correct-support'; supportId: string; body: Schema['CorrectAuthoredSupportRequest'] }
  | { action: 'record-event'; supportId: string; body: Schema['RecordSupportEventRequest'] }
  | { action: 'record-followup'; playerId: string; body: Schema['RecordCanonicalFollowupRequest'] };
export type CoachingManualResult<C extends CoachingManualCommand = CoachingManualCommand> =
  C extends { action: 'create-goal-from-feedback' } ? Schema['GoalFromFeedbackResult']
  : C extends { action: 'select-focus' } ? Schema['CanonicalFocusResponse']
  : C extends { action: 'record-event' } ? Schema['SupportEventResult']
  : C extends { action: 'record-followup' } ? Schema['CanonicalFollowupResult']
  : Schema['AuthoredSupportResult'];
export type CoachingManualState<C extends CoachingManualCommand> =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: CoachingManualResult<C> };
export type CoachingManualBoundary = { current(): boolean; dispose(): void };
export type CoachingManualFailure = 'rejected' | 'retired' | 'unknown';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const uuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const version = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 2147483647;
const decimal = (value: unknown): value is string => typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value)
  && BigInt(value) <= 9223372036854775807n;
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const keys = (value: object, expected: string) => Object.keys(value).sort().join(',') === expected.split(',').sort().join(',');
const stamp = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value));
function sameReference(a: Schema['CoachingCanonicalFeedbackReference'] | Schema['CoachingCanonicalGoalReference'],
  b: Schema['CoachingCanonicalFeedbackReference'] | Schema['CoachingCanonicalGoalReference']): boolean {
  return keys(a, 'source_kind,player_id,source_revision,proof_schema,goal_id,feedback_id,feedback_revision_id,feedback_version')
    && typeof a.player_id === 'string' && /^[0-9a-f]{64}$/.test(a.source_revision)
    && (a.source_kind === 'goal' ? decimal(a.goal_id) && a.feedback_id === null && a.feedback_revision_id === null && a.feedback_version === null
      : a.source_kind === 'feedback' && a.goal_id === null && uuid(a.feedback_id) && uuid(a.feedback_revision_id) && version(a.feedback_version))
    && a.source_kind === b.source_kind && a.player_id === b.player_id && a.source_revision === b.source_revision
    && a.proof_schema === b.proof_schema && (a.goal_id ?? null) === (b.goal_id ?? null)
    && (a.feedback_id ?? '').toLowerCase() === (b.feedback_id ?? '').toLowerCase()
    && (a.feedback_revision_id ?? '').toLowerCase() === (b.feedback_revision_id ?? '').toLowerCase()
    && (a.feedback_version ?? null) === (b.feedback_version ?? null);
}

function acknowledgement<C extends CoachingManualCommand>(command: C, value: CoachingManualResult): CoachingManualResult<C> {
  const fail = (): never => { throw new Error('invalid_manual_acknowledgement'); };
  if (!value || typeof value !== 'object') return fail();
  if (command.action === 'create-goal-from-feedback') {
    if (value.schema_version !== 'goal-from-feedback-result.v1' || !keys(value, 'schema_version,goal_id')
      || !decimal(value.goal_id)) return fail();
  } else if (command.action === 'select-focus') {
    if (value.schema_version !== 'canonical-focus.v1' || !keys(value, 'schema_version,focus')) return fail();
    const focus = value.focus;
    if (!focus || !keys(focus, 'id,activity_id,player_id,selected_by,created_at,selected_at,revision,status,reference')
      || typeof focus.id !== 'string' || !focus.id || [...focus.id].length > 128
      || focus.activity_id !== command.activityId || focus.player_id !== command.playerId
      || typeof focus.selected_by !== 'string' || !focus.selected_by || !stamp(focus.created_at) || !stamp(focus.selected_at)
      || !decimal(focus.revision) || BigInt(focus.revision) <= BigInt(command.body.expected_revision)
      || focus.status !== 'current' || !focus.reference || !sameReference(focus.reference, command.body.reference)) return fail();
  } else if (command.action === 'record-followup') {
    if (value.schema_version !== 'canonical-followup-result.v1' || !keys(value, 'schema_version,followup_id,event_id')
      || !uuid(value.followup_id) || !decimal(value.event_id)) return fail();
  } else if (command.action === 'record-event') {
    if (value.schema_version !== 'support-event-result.v1'
      || !keys(value, 'schema_version,support_id,revision_id,event_id,version,kind,effect')
      || !uuid(value.support_id) || !same(value.support_id, command.supportId)
      || !uuid(value.revision_id) || !same(value.revision_id, command.body.expected_revision_id)
      || !uuid(value.event_id) || !version(value.version) || value.version !== command.body.expected_version
      || !['observation_added', 'observation_corrected'].includes(value.kind) || !['recorded', 'unchanged'].includes(value.effect)) return fail();
  } else {
    if (value.schema_version !== 'authored-support-result.v1' || !keys(value, 'schema_version,support_id,revision_id,version,effect')
      || !uuid(value.support_id) || !uuid(value.revision_id) || !version(value.version)) return fail();
    if (command.action === 'create-support') {
      if (value.version !== 1 || value.effect !== 'created') return fail();
    } else if (!same(value.support_id, command.supportId)
      || (value.effect === 'unchanged' ? value.version !== command.body.expected_version || !same(value.revision_id, command.body.expected_revision_id)
        : value.effect !== 'corrected' || value.version !== command.body.expected_version + 1 || same(value.revision_id, command.body.expected_revision_id))) return fail();
  }
  return clone(value) as CoachingManualResult<C>;
}

export function captureCoachingManualOperation<C extends CoachingManualCommand>(transport: CoachingTransport,
  scope: CoachingScopeIntent, command: C, boundary: CoachingManualBoundary,
  options: { classifyFailure?: (error: unknown) => CoachingManualFailure; onAccepted?: () => void | Promise<void> } = {}) {
  assertSafeWireNumbers(command);
  const original = clone(command);
  const client = coachingManualClient(transport, clone(scope));
  let state: CoachingManualState<C> = { status: 'ready' };
  let unresolved = false;
  let disposed = false;
  let inFlight: Promise<CoachingManualResult<C>> | null = null;
  const current = () => !disposed && boundary.current();
  const assertCurrent = () => { if (!current()) throw new Error('manual_context_changed'); };
  async function run(): Promise<CoachingManualResult<C>> {
    state = { status: 'sending' };
    try {
      assertCurrent();
      const intent = clone(original);
      const value = intent.action === 'create-goal-from-feedback' ? await client.createGoalFromFeedback(intent.body)
        : intent.action === 'select-focus' ? await client.selectFocus(intent.activityId, intent.playerId, intent.body)
        : intent.action === 'create-support' ? await client.createSupport(intent.body)
        : intent.action === 'correct-support' ? await client.correctSupport(intent.supportId, intent.body)
        : intent.action === 'record-event' ? await client.recordSupportEvent(intent.supportId, intent.body)
        : await client.recordFollowup(intent.playerId, intent.body);
      assertCurrent();
      const result = acknowledgement(original, value);
      state = { status: 'accepted', result }; unresolved = false;
      // A separate refresh never changes the accepted result or chains a write.
      void Promise.resolve().then(() => { if (current()) return options.onAccepted?.(); }).catch(() => {});
      return clone(result);
    } catch (error) {
      const classification = options.classifyFailure?.(error) ?? 'unknown';
      if (classification !== 'rejected') unresolved = true;
      state = classification === 'retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      // Never expose an earlier lifetime's error to a new authority.
      assertCurrent();
      throw error;
    } finally { inFlight = null; }
  }
  return {
    getIntent(): C | null { return current() ? clone(original) : null; },
    getState(): CoachingManualState<C> { return current() ? clone(state) : { status: 'unavailable' }; },
    send(): Promise<CoachingManualResult<C>> {
      try { assertCurrent(); } catch (error) { return Promise.reject(error); }
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve(clone(state.result));
      // Defer dispatch until inFlight is installed, including sync throws.
      inFlight = Promise.resolve().then(run);
      return inFlight;
    },
    dispose(): void { disposed = true; boundary.dispose(); },
  };
}
